import {
  colorContentDim,
  colorBaseNeutral700,
} from '@duxton-web/design-tokens/themes/light';
import { AddUniversal, KnowledgeUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Dropdown,
  Flex,
  Form,
  Input,
  Segmented,
  Select,
  Switch,
  Typography,
} from '@duxton-web/ui';
import React, { useMemo } from 'react';

import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';
import DynamicAttributesSelector from 'omega-rtc/components/custom/entities/DynamicAttributesSelector';
import SmartCampaignCallToAction from 'omega-rtc/components/form/ucd/SmartCampaignCallToActions';
import SmartCampaignDynamicAttributesCard from 'omega-rtc/components/form/ucd/SmartCampaignDynamicAttributesCard';
import SmartCampaignImageUpload from 'omega-rtc/components/form/ucd/SmartCampaignImageUpload';
import {
  channelOptions,
  CONTENT_METADATA_OPTION_KEYS,
  PN_IMAGE_KEY,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES,
  SMART_CAMPAIGN_CONTENT_SOURCE_OPTIONS,
  SMART_CAMPAIGN_FORM_KEYS,
} from 'omega-rtc/constants/ucd';
import { useSelector } from 'react-redux';
import { getSlangName } from 'omega-rtc/utils/smart-campaign-utils';

const SmartCampaignMetaDataContents = ({
  isCVPOthers,
  callToActionOptions,
  handleAddNewItemInCallToAction,
  contentMetaDataState,
  watchChannel,
  updateImageData,
  setContentMetaDataState,
  imageDataForEachLanguage,
}) => {
  const {
    primaryLanguage,
    isMystiqueContentEmpty,
    isMystiqueCallPending,
    setPrimaryLanguageContentsInForm,
    isPrimaryLanguageContentsEmpty,
    isCurrentContentSourceManual,
  } = useCreateSmartCampaign();
  const form = Form.useFormInstance();
  const watchDynamicAttribute = Form.useWatch(
    SMART_CAMPAIGN_FORM_KEYS.DYNAMIC_ATTRIBUTE,
    form,
  );

  // Condition to check if watchDynamicAttribute has items
  const hasDynamicAttributes = useMemo(
    () =>
      Array.isArray(watchDynamicAttribute) && !!watchDynamicAttribute.length,
    [watchDynamicAttribute],
  );

  const projectView = useSelector((state) => state.ucd.projectView);

  const countryID =
    projectView && projectView.project && projectView.project.countryID;
  const slangName = useMemo(() => {
    if (!countryID) {
      return null;
    }

    return getSlangName({
      countryID,
      language: primaryLanguage,
    });
  }, [countryID, primaryLanguage]);

  return (
    <Flex vertical>
      <Form.Item
        label="Format"
        required
        name={SMART_CAMPAIGN_FORM_KEYS.CHANNEL}
        rules={[{ message: 'Please select a format', required: true }]}
        initialValue={SMART_CAMPAIGN_CHANNELS.push}
      >
        <Select
          placeholder="Select channel"
          options={channelOptions}
          showSearch
        />
      </Form.Item>
      <Form.Item
        label="Content source"
        required
        name={SMART_CAMPAIGN_FORM_KEYS.CONTENT_SOURCE}
        initialValue={SMART_CAMPAIGN_CONTENT_SOURCE_MODES.automatic}
        rules={[{ message: 'Please select a content source', required: true }]}
      >
        <Segmented
          options={SMART_CAMPAIGN_CONTENT_SOURCE_OPTIONS}
          disabled={isMystiqueCallPending || !isPrimaryLanguageContentsEmpty}
          onChange={(selectedValue) => {
            setPrimaryLanguageContentsInForm((prev) => ({
              ...prev,
              mode: selectedValue,
            }));
          }}
        />
      </Form.Item>
      <React.Fragment>
        <Form.Item
          required
          hidden={!isCVPOthers || isCurrentContentSourceManual}
          label="Product Overview"
          name={SMART_CAMPAIGN_FORM_KEYS.PRODUCT_OVERVIEW}
          rules={[{ message: 'Please input product overview', required: true }]}
        >
          <Input.TextArea
            placeholder="Enter product overview"
            autoSize={{ minRows: 5, maxRows: 10 }}
          />
        </Form.Item>
        <Form.Item
          required
          label="Reason to Use"
          hidden={!isCVPOthers || isCurrentContentSourceManual}
          name={SMART_CAMPAIGN_FORM_KEYS.REASON_TO_USE}
          rules={[{ message: 'Please input reason to use', required: true }]}
        >
          <Input.TextArea
            placeholder="Enter reason to use"
            autoSize={{ minRows: 5, maxRows: 10 }}
          />
        </Form.Item>
      </React.Fragment>
      <Form.Item
        required
        label="Jobs-to-be-done"
        hidden={isCurrentContentSourceManual}
        name={SMART_CAMPAIGN_FORM_KEYS.JTBD}
        rules={[{ message: 'Please input Jobs-to-be-done', required: true }]}
        extra={
          <Typography.Metadata level={1} style={{ color: colorContentDim }}>
            Get, To, By
          </Typography.Metadata>
        }
      >
        <Input.TextArea
          placeholder="Enter jobs-to-be-done"
          autoSize={{ minRows: 5, maxRows: 10 }}
        />
      </Form.Item>

      {/* Call To Action Selector */}
      <SmartCampaignCallToAction
        callToActionOptions={callToActionOptions}
        handleAddNewItemInCallToAction={handleAddNewItemInCallToAction}
        isHidden={!isCVPOthers || isCurrentContentSourceManual}
      />
      {/* Dynamic Attributes Selector */}
      <DynamicAttributesSelector hasDynamicAttributes={hasDynamicAttributes} />
      {hasDynamicAttributes ? (
        <SmartCampaignDynamicAttributesCard
          dynamicAttribute={watchDynamicAttribute}
        />
      ) : (
        <Alert
          closable
          showIcon
          icon={<KnowledgeUniversal />}
          style={{ padding: 16, gap: 8, marginBottom: 24 }}
          message="Pro tip: Adding dynamic attributes increased open rates by >50%"
          description="Personalise your content to increase its relevance and open rate!"
        />
      )}
      {Boolean(slangName) && !isCurrentContentSourceManual && (
        <Flex gap={8} align="center" style={{ marginBottom: 24 }}>
          <Form.Item name={SMART_CAMPAIGN_FORM_KEYS.SLANG_TOGGLE} noStyle>
            <Switch
              disabled={isMystiqueCallPending}
            />
          </Form.Item>
          <Typography.Body level={2} style={{ color: colorBaseNeutral700 }}>
            Include {slangName} version
          </Typography.Body>
        </Flex>
      )}
      {!isMystiqueContentEmpty && (
        <Form.Item
          name={SMART_CAMPAIGN_FORM_KEYS.FEEDBACK}
          label="Feedback"
          hidden={isCurrentContentSourceManual}
          required
          rules={[{ message: 'Please input feedback', required: true }]}
        >
          <Input.TextArea
            placeholder="Feedback"
            autoSize={{ minRows: 5, maxRows: 10 }}
          />
        </Form.Item>
      )}
      {contentMetaDataState.showKeyPointer && (
        <Form.Item
          label="Key pointers"
          name={SMART_CAMPAIGN_FORM_KEYS.KEY_POINTER}
        >
          <Input.TextArea
            placeholder="Enter key pointers"
            autoSize={{ minRows: 5, maxRows: 10 }}
          />
        </Form.Item>
      )}
      {watchChannel === SMART_CAMPAIGN_CHANNELS.push &&
        contentMetaDataState.showImageField && (
          <Flex
            vertical
            style={{
              paddingBottom:
                !!contentMetaDataState.metaDataOptions.length && '24px',
            }}
          >
            <Form.Item
              label="Image"
              style={{ marginBottom: '8px' }}
              name={SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL}
            >
              {/* TODO: Need to hide the input field feature */}
              {/* <Input
                placeholder="Enter image url"
                onChange={(e) => {
                  const imageUrl = e.target.value;
                  
                    // isImageUrlValid(imageUrl) -> Used to check the given URL is valid
                    // Note:
                    // Network call is happened for valid URL's
                  
                  if (isImageUrlValid(imageUrl)) {
                    getImageInfoFromUrl(imageUrl, (imageData) => {
                      updateImageData({
                        imageData,
                      });
                    });
                  }
                }} */}
              {/* /> */}
              <SmartCampaignImageUpload
                imageData={imageDataForEachLanguage[PN_IMAGE_KEY]}
                handleImageDataChange={(imageData) => {
                  updateImageData({
                    imageData,
                  });
                  form.setFieldValue(
                    SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL,
                    (imageData && imageData.url) || '',
                  );
                }}
              />
            </Form.Item>
          </Flex>
        )}
      {!!contentMetaDataState.metaDataOptions.length &&
        !isCurrentContentSourceManual && (
          <Dropdown
            menu={{
              items: contentMetaDataState.metaDataOptions,
              onClick: (item) => {
                setContentMetaDataState((previousState) => ({
                  ...previousState,
                  metaDataOptions: previousState.metaDataOptions.filter(
                    (option) => option.key !== item.key,
                  ),
                  showImageField:
                    previousState.showImageField ||
                    item.key === CONTENT_METADATA_OPTION_KEYS.image,
                  showKeyPointer:
                    previousState.showKeyPointer ||
                    item.key === CONTENT_METADATA_OPTION_KEYS.keyPointer,
                }));
              },
            }}
            overlayStyle={{ width: '175px' }}
            trigger={['click']}
          >
            <Button
              size="medium"
              style={{ fontSize: 14, fontWeight: 600, width: 'max-content' }}
            >
              Add
              <AddUniversal style={{ fontSize: 16 }} />
            </Button>
          </Dropdown>
        )}
    </Flex>
  );
};

export default SmartCampaignMetaDataContents;
